import { useCallback, useEffect, useState } from "react";
import { Building2, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { AppShell } from "@/components/app/AppShell";
import { CopyLink } from "@/components/app/CopyLink";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FILTERS = ["pending", "approved", "declined", "all"];
const BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-slate-100 text-slate-600 border-slate-200",
};

const Field = ({ label, value }) => (
  <div>
    <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{label}</div>
    <div className="mt-1 break-words text-sm text-oath-ink">{value || "—"}</div>
  </div>
);

const Stat = ({ label, value, id }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`stat-${id}`}>
    <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{label}</div>
    <div className="mt-2 font-display text-3xl font-bold text-oath-ink">{value ?? "—"}</div>
  </div>
);

const ApprovePanel = ({ req, onDone, onCancel }) => {
  const [orgName, setOrgName] = useState(req.company);
  const [loading, setLoading] = useState(false);
  const approve = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/access-requests/${req.id}/approve`, { org_name: orgName });
      toast.success(`${data.org.name} created`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="approve-panel">
      <div className="font-mono-label text-[10px] font-semibold text-emerald-800">CREATE CLIENT COMPANY</div>
      <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="mt-2 bg-white" data-testid="approve-org-name-input" />
      <p className="mt-2 text-xs text-oath-secondary">{req.full_name} ({req.email}) will be invited as Org Admin.</p>
      <div className="mt-3 flex gap-2">
        <button onClick={approve} disabled={loading || orgName.trim().length < 2} data-testid="approve-confirm-button"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve & create invite
        </button>
        <button onClick={onCancel} className="rounded-full px-4 py-2 text-xs text-oath-secondary hover:bg-white" data-testid="approve-cancel-button">Cancel</button>
      </div>
    </div>
  );
};

const DeclinePanel = ({ req, onDone, onCancel }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const decline = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/access-requests/${req.id}/decline`, { reason });
      toast.success("Request declined");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-5 rounded-2xl border border-red-200 bg-red-50/40 p-4" data-testid="decline-panel">
      <div className="font-mono-label text-[10px] font-semibold text-red-800">REASON (INTERNAL)</div>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2 bg-white" rows={3} data-testid="decline-reason-input" />
      <div className="mt-3 flex gap-2">
        <button onClick={decline} disabled={loading || reason.trim().length < 3} data-testid="decline-confirm-button"
          className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Decline request
        </button>
        <button onClick={onCancel} className="rounded-full px-4 py-2 text-xs text-oath-secondary hover:bg-white" data-testid="decline-cancel-button">Cancel</button>
      </div>
    </div>
  );
};

const RequestCard = ({ req, onChanged }) => {
  const [mode, setMode] = useState(null);
  const [result, setResult] = useState(null);
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`request-card-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-oath-accent-light text-oath-accent"><Building2 className="h-5 w-5" /></span>
          <div>
            <h3 className="font-display text-lg font-bold text-oath-ink">{req.company}</h3>
            <p className="text-sm text-oath-secondary">{req.full_name} · {req.role} · {req.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full border px-2.5 py-0.5 font-mono-label text-[9px] font-semibold ${BADGE[req.status]}`} data-testid="request-status-badge">
            {req.status.toUpperCase()}
          </span>
          <span className="font-mono text-[11px] text-oath-muted">{fmtDate(req.created_at)}</span>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-4">
        <Field label="INDUSTRY" value={req.industry} />
        <Field label="LICENSE #" value={req.license_number} />
        <Field label="MONTHLY VOLUME" value={req.monthly_volume} />
        <Field label="PHONE" value={req.phone} />
        <Field label="SMS CONSENT" value={req.sms_consent ? `Yes · ${fmtDate(req.sms_consent_at)}` : "No"} />
      </div>
      <div className="mt-4"><Field label="INTENDED USE" value={req.intended_use} /></div>
      {req.decline_reason && <div className="mt-4"><Field label="DECLINE REASON" value={req.decline_reason} /></div>}
      {req.status === "pending" && !mode && !result && (
        <div className="mt-5 flex gap-2">
          <button onClick={() => setMode("approve")} data-testid="request-approve-button"
            className="rounded-full bg-oath-ink px-4 py-2 text-xs font-semibold text-white hover:bg-black">Approve</button>
          <button onClick={() => setMode("decline")} data-testid="request-decline-button"
            className="rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold text-oath-ink hover:bg-gray-50">Decline</button>
        </div>
      )}
      {mode === "approve" && !result && <ApprovePanel req={req} onCancel={() => setMode(null)} onDone={(d) => { setResult(d); setMode(null); }} />}
      {mode === "decline" && <DeclinePanel req={req} onCancel={() => setMode(null)} onDone={onChanged} />}
      {result && (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="approve-result">
          <p className="text-sm text-oath-ink">Approved. Send this invite link to {result.invite.email} (valid 7 days):</p>
          <div className="mt-3"><CopyLink path={result.invite.invite_path} testid="approve-invite-link" /></div>
          <button onClick={onChanged} className="mt-3 text-xs text-oath-accent underline" data-testid="approve-done-button">Done</button>
        </div>
      )}
    </article>
  );
};

export default function AdminInbox() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [r, s] = await Promise.all([api.get(`/admin/access-requests?status=${filter}`), api.get("/admin/stats")]);
      setItems(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Access requests">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat id="pending" label="PENDING REQUESTS" value={stats.pending_requests} />
        <Stat id="optins" label="ACTIVE OPT-INS" value={stats.opt_ins} />
        <Stat id="orgs" label="CLIENT COMPANIES" value={stats.orgs} />
        <Stat id="users" label="USERS" value={stats.users} />
      </div>
      <div className="mt-8 flex flex-wrap gap-2" data-testid="inbox-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`inbox-filter-${f}`}
            className={`rounded-full px-4 py-2 font-mono-label text-[10px] font-semibold transition-colors ${
              filter === f ? "bg-oath-ink text-white" : "border border-gray-200 bg-white text-oath-secondary hover:border-gray-300"
            }`}>
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="mt-5 grid gap-4" data-testid="inbox-list">
        {items === null && <Loader2 className="h-5 w-5 animate-spin text-oath-muted" />}
        {items?.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white/60 p-10 text-center text-sm text-oath-muted" data-testid="inbox-empty">
            No {filter === "all" ? "" : filter} requests.
          </div>
        )}
        {items?.map((r) => <RequestCard key={r.id} req={r} onChanged={load} />)}
      </div>
    </AppShell>
  );
}
